import {useCallback} from 'react';

import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {clearCellsInRange as clearCellsInRangeMutation} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {updateCell as updateCellMutation} from 'notebook/tables/store/logic/mutations/updateCell';
import type {
  Cell,
  InsertableColumnInfo,
  MultiSelectionBox,
  TableIndex,
  TableOperation,
} from 'notebook/tables/TableConstants';
import {
  trackCrossStepReferenceChange,
  trackCrossStepReferenceBulkDelete,
} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

interface TableOpsMethods {
  renameTable: (index: number, newTableName: string) => void;
  performTableOp?: (tableOp: TableOperation) => void;
  deleteColumns: (index: number, start: number, end: number) => void;
  insertColumnsLeft: (
    index: number,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  insertColumnsRight: (
    index: number,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  deleteRows: (index: number, start: number, end: number) => void;
  deleteRowsNonContiguous: (index: number, ranges: Array<[number, number]>) => void;
  insertRowsAbove: (index: number, start: number, end: number) => void;
  insertRowsBelow: (index: number, start: number, end: number) => void;
  mergeRegion: ((index: number, selectedBox: MultiSelectionBox) => void) | null;
  unmergeRegion: ((index: number, selectedBox: MultiSelectionBox) => void) | null;
  reorderRowsOrColumns: (index: number, options: ReorderOptions) => void;
}

export interface TableMutationMethods extends TableOpsMethods {
  /**
   * Update a single cell in the table.
   *
   * @param cellIndex - The [row, column] index of the cell to update
   * @param cellData - Partial cell data to merge into the existing cell
   * @param expandTable - If true, add a new blank row at the bottom
   */
  updateCell: (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean) => void;

  /**
   * Clear all cells within a selection box.
   *
   * @param selectionBox - The box defining which cells to clear
   */
  clearCellsInRange: (selectionBox: MultiSelectionBox) => void;
}

/**
 * Hook that provides table mutation methods with analytics and persistence.
 *
 * All data is fetched fresh from the TableModel at execution time,
 * ensuring we always use the latest state and keeping callbacks stable.
 */
export function useTableMutations(): TableMutationMethods {
  const {useTableEditableStore, tableModelSelector} = TableModelContext.useContext();
  const tableOps = useTableOps();
  const {tableModel: disableMergingCells} = useTableModel((state) => state.props.disableMergingCells);

  const getTableModel = useCallback(
    () => tableModelSelector(useTableEditableStore.getState()),
    [useTableEditableStore, tableModelSelector]
  );

  const updateCell = useCallback(
    (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;
      const {singleInputBoundaryIndex} = model.structure;

      // Execute pure mutation
      const result = updateCellMutation(table, cellIndex, cellData, {
        expandTable,
        singleInputBoundaryIndex,
      });

      // Track cross-step reference analytics
      if (cellData.text != null) {
        const oldCellText = result.affectedCells[0]?.oldCell?.text ?? '';
        const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
        const worksheetInfo = worksheetId
          ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
          : null;
        trackCrossStepReferenceChange(oldCellText, cellData.text, worksheetInfo);
      }

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      const newCell = result.affectedCells[0]?.newCell;
      if (newCell) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE, cells: [newCell]},
        ]);
      }
    },
    [getTableModel]
  );

  const clearCellsInRange = useCallback(
    (selectionBox: MultiSelectionBox): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;

      // Execute pure mutation
      const result = clearCellsInRangeMutation(table, selectionBox);

      // Track cross-step reference analytics
      const oldCells = result.affectedCells.map((c) => c.oldCell).filter((c): c is Cell => c != null);
      const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
      const worksheetInfo = worksheetId
        ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
        : null;
      trackCrossStepReferenceBulkDelete(oldCells, worksheetInfo);

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      if (oldCells.length > 0) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {
            event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE,
            cells: oldCells,
          },
        ]);
      }
    },
    [getTableModel]
  );

  return {
    updateCell,
    clearCellsInRange,
    renameTable: tableOps.renameTable,
    performTableOp: tableOps.performTableOp,
    deleteColumns: tableOps.deleteTableColumns,
    insertColumnsLeft: tableOps.insertTableColumnsLeft,
    insertColumnsRight: tableOps.insertTableColumnsRight,
    deleteRows: tableOps.deleteTableRows,
    deleteRowsNonContiguous: tableOps.deleteTableRowsNonContiguous,
    insertRowsAbove: tableOps.insertTableRowsAbove,
    insertRowsBelow: tableOps.insertTableRowsBelow,
    mergeRegion: disableMergingCells ? null : tableOps.mergeTableRegion,
    unmergeRegion: disableMergingCells ? null : tableOps.unmergeTableRegion,
    reorderRowsOrColumns: tableOps.reorderTableRowsOrColumns,
  };
}
